/**
 * Live topics: which cached queries an outbox event makes stale (developer.md ADR-026).
 *
 * The server publishes events on fixed topics (backend `core/registries/events.py`); this registry
 * maps each topic to the TanStack Query key prefixes to invalidate. Events only say *what* changed,
 * never the new state: invalidating makes the visible queries refetch from the API, which decides
 * what the person may read. Never render anything from `event.data`.
 *
 * Feature modules add their own keys next to their query hooks:
 *
 *   registerLiveTopic("sales_order", (event, slug) => [["tenant", slug, "sales-orders"]]);
 *
 * Several handlers per topic are fine (their keys are combined); `registerLiveTopic` returns an
 * unregister function for tests. Keep prefixes coarse: refetching an extra visible list is cheap,
 * a stale screen is not.
 */
import type { QueryKey } from "@tanstack/react-query";

import type { components } from "@/api/generated/schema";

export type LiveEvent = components["schemas"]["OutboxEvent"];
export type LiveTopic = components["schemas"]["EventTopicEnum"];
export type TopicHandler = (event: LiveEvent, slug: string) => QueryKey[];

const handlers = new Map<LiveTopic, Set<TopicHandler>>();

export function registerLiveTopic(topic: LiveTopic, handler: TopicHandler): () => void {
  const set = handlers.get(topic) ?? new Set<TopicHandler>();
  set.add(handler);
  handlers.set(topic, set);
  return () => {
    set.delete(handler);
  };
}

/** The distinct query keys a batch of events makes stale. */
export function staleKeys(events: LiveEvent[], slug: string): QueryKey[] {
  const keys = new Map<string, QueryKey>();
  for (const event of events) {
    for (const handler of handlers.get(event.topic) ?? []) {
      for (const key of handler(event, slug)) keys.set(JSON.stringify(key), key);
    }
  }
  return [...keys.values()];
}

// --- defaults for the areas that exist today (api/queries/*.ts key shapes) ----------------------------------
const tenant = (slug: string, ...rest: string[]): QueryKey => ["tenant", slug, ...rest];

const jobs: TopicHandler = (_e, slug) => [tenant(slug, "sales", "jobs"), tenant(slug, "jobs")];
const inventory: TopicHandler = (_e, slug) => [tenant(slug, "inventory")];
const purchasing: TopicHandler = (_e, slug) => [tenant(slug, "purchasing")];

registerLiveTopic("job", jobs);
registerLiveTopic("phase", jobs);
registerLiveTopic("readiness", jobs);
registerLiveTopic("field_issue", jobs);
registerLiveTopic("sales_order", (_e, slug) => [tenant(slug, "sales-orders"), tenant(slug, "sales", "jobs")]);
registerLiveTopic("requirement", inventory);
registerLiveTopic("stock", inventory);
registerLiveTopic("reservation", inventory);
registerLiveTopic("purchase_order", purchasing);
registerLiveTopic("delivery", purchasing);
registerLiveTopic("purchasing_run", purchasing);
registerLiveTopic("discrepancy", purchasing);
registerLiveTopic("receiving", (_e, slug) => [tenant(slug, "purchasing"), tenant(slug, "inventory")]);
registerLiveTopic("work_order", (_e, slug) => [tenant(slug, "work-orders")]);
registerLiveTopic("crew", (_e, slug) => [tenant(slug, "schedule"), tenant(slug, "crews")]);
registerLiveTopic("schedule", (_e, slug) => [tenant(slug, "schedule")]);
registerLiveTopic("invoice", (_e, slug) => [tenant(slug, "billing")]);
registerLiveTopic("payment", (_e, slug) => [tenant(slug, "billing")]);
registerLiveTopic("notification", (_e, slug) => [tenant(slug, "notifications")]);
// "preference" is handled by LiveUpdates itself: it must not overwrite a change still being saved.
